/**
 * Detail Queue — the contractor's "cars to detail" screen (formerly "Melville").
 *
 * Named generically because it can serve any dealership; the current
 * dealership shows as context in the kicker. Redesigned per the Claude-Design
 * handoff: a teal intro block (date · dealership, Detail Queue / Completed
 * tabs, search), day chips, and full-bleed brand-logo cards. Keeps the pick →
 * In Progress → Submit timing flow and the location-availability lock.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import { MagnifyingGlass } from '@phosphor-icons/react';

const NAVY = '#1e3a4a', LINE = '#e5e7eb', MUTED = '#6b7280';
const TEAL = '#4A7A91';
const PUB  = process.env.PUBLIC_URL;

// Current dealership context (kicker). TODO: derive from the worker's roster
// once multi-dealership scheduling exists; Melville for now.
const DEALERSHIP = 'Melville';

const BRAND_LOGO = {
  KIA: `${PUB}/brand-logos/kia.png`, SUZUKI: `${PUB}/brand-logos/suzuki.png`,
  GWM: `${PUB}/brand-logos/gwm.png`, GAC: `${PUB}/brand-logos/gac.png`,
  USED: `${PUB}/brand-logos/used.png`, 'USED CARS': `${PUB}/brand-logos/used.png`,
};
const DEAL_COLOR = { SOLD: '#2563eb', CUSTOMER: '#7c3aed', DEMO: '#6d28d9', STOCK: '#9ca3af', USED: '#b91c1c' };
// Queue priority (matches the Melville dashboard): deliveries (SOLD) first,
// then DEMO, then the rest — ties broken by delivery time.
const DEAL_ORDER = { SOLD: 0, DEMO: 1, CUSTOMER: 2, STOCK: 3, USED: 4 };
// Colour word tint on the model line.
function colourTint(c) {
  const k = String(c || '').toLowerCase();
  if (/red|vermelh/.test(k)) return '#dc2626';
  if (/blue|azul/.test(k)) return '#2563eb';
  if (/green|verde/.test(k)) return '#16a34a';
  if (/black|preto/.test(k)) return '#374151';
  return '#9ca3af';
}

const LOCATION_LABELS = { south: 'South St', garling: 'Garling St', melville: 'Melville', offside: 'Offside' };
const isAvailable = (loc) => String(loc || '').toLowerCase() === 'melville';

const weekBtn = { flex: '0 0 auto', width: 40, height: 40, borderRadius: 12, border: 'none', background: '#fff', color: '#1e3a4a', fontSize: 20, fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' };

const WD = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const MO = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
function kickerDate(key) {
  const [y, m, d] = key.split('-').map(Number);
  return `${WD[new Date(y, m - 1, d).getDay()]} ${d} ${MO[m - 1]}`;
}
function fmtDur(s) {
  if (s == null) return '—';
  s = Math.max(0, Math.round(s));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  return h ? `${h}h ${String(m).padStart(2, '0')}m` : m ? `${m}m` : `${s}s`;
}
const timeHM = (iso) => { if (!iso) return ''; const d = new Date(iso); return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; };

// ── Week helpers (Monday-start weeks) ──────────────────────────────────────
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
function mondayOf(date) {
  const x = new Date(date); x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); // 0 = Monday
  return x;
}
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
function weekLabel(monday) {
  const end = addDays(monday, 6);
  const sameMonth = monday.getMonth() === end.getMonth();
  return sameMonth
    ? `${monday.getDate()}–${end.getDate()} ${MO[monday.getMonth()]}`
    : `${monday.getDate()} ${MO[monday.getMonth()]} – ${end.getDate()} ${MO[end.getMonth()]}`;
}

// Car-completed mark (front car + check above) — recreated from the supplied
// icon as inline SVG so it needs no asset.
function CarCheckIcon({ size = 26, color = '#15803d' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" style={{ flex: 'none' }} aria-label="Completed">
      <path d="M17 13 l4.5 4.5 L31 8" stroke={color} strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M15 23 H33 L36 31 H12 Z" stroke={color} strokeWidth="3" strokeLinejoin="round" />
      <rect x="10.5" y="31" width="27" height="11" rx="2.5" stroke={color} strokeWidth="3" />
      <circle cx="17.5" cy="37" r="2.3" fill={color} />
      <circle cx="30.5" cy="37" r="2.3" fill={color} />
    </svg>
  );
}

// Small brand mark: logo when we have one, else a text chip.
function BrandMark({ brand, avail }) {
  const logo = BRAND_LOGO[brand];
  if (logo) {
    return <img src={logo} alt={brand} style={{ width: 46, height: 42, objectFit: 'contain', flex: 'none', filter: avail ? 'none' : 'grayscale(1) opacity(0.6)' }} />;
  }
  return (
    <div style={{ width: 46, height: 42, flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#eef2f5', borderRadius: 8 }}>
      <span style={{ fontSize: 9.5, fontWeight: 800, color: avail ? NAVY : '#94a3b8' }}>{brand}</span>
    </div>
  );
}

// Time on the card counts from Start to Finish, minus anything paused. Shown
// coarse on purpose — this is "how long has this car been open", a glance, not
// a stopwatch anyone should feel measured by.
function elapsedLabel(v, nowMs) {
  if (!v.started_at) return null;
  const paused  = (v.paused_seconds || 0) + (v.paused_at ? (nowMs - new Date(v.paused_at).getTime()) / 1000 : 0);
  const secs    = Math.max(0, (nowMs - new Date(v.started_at).getTime()) / 1000 - paused);
  const mins    = Math.floor(secs / 60);
  return mins < 60 ? `${mins}m` : `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, '0')}m`;
}

export default function MelvilleQueue({ onPickCar, isAdmin }) {
  const [vehicles, setVehicles] = useState([]);
  // Re-render every 30s so the elapsed label doesn't sit frozen. Minute
  // resolution — a per-second tick would be noise on a card this small.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);
  const [configured, setConfigured] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDay, setSelectedDay] = useState(null);
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [view, setView] = useState('active');      // 'active' | 'completed'
  const [search, setSearch] = useState('');
  const [timings, setTimings] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await api.melvilleDetailQueue();
      setConfigured(r.configured !== false);
      setVehicles(r.vehicles || []);
      if (r.error) setError(r.error);
      if (isAdmin) { try { const t = await api.melvilleTimings(); setTimings(t.timings || []); } catch { /* ignore */ } }
    } catch (err) {
      setError(err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);
  useEffect(() => { load(); }, [load]);

  const weekStartKey = ymd(weekStart);
  const weekEndKey = ymd(addDays(weekStart, 6));
  const inWeek = (key) => key >= weekStartKey && key <= weekEndKey;

  useEffect(() => {
    if (view === 'completed') api.melvilleMyCompleted(weekStartKey, weekEndKey).then(r => setCompleted(r.completed || [])).catch(() => setCompleted([]));
  }, [view, weekStartKey, weekEndKey]);

  // Group active queue by delivery day.
  const byDay = {};
  for (const v of vehicles) {
    const k = v.delivery_date ? String(v.delivery_date).slice(0, 10) : 'none';
    (byDay[k] || (byDay[k] = [])).push(v);
  }
  // Day chips are limited to the selected week; undated cars ('none') always show.
  const dayKeys = Object.keys(byDay).filter(k => k !== 'none' && inWeek(k)).sort();
  if (byDay.none) dayKeys.push('none');

  useEffect(() => {
    const keys = [...new Set(vehicles.map(v => v.delivery_date ? String(v.delivery_date).slice(0, 10) : 'none'))]
      .filter(k => k !== 'none' && k >= weekStartKey && k <= weekEndKey).sort();
    if (vehicles.some(v => !v.delivery_date)) keys.push('none');
    if (!keys.length) { setSelectedDay(null); return; }
    const todayKey = ymd(new Date());
    setSelectedDay(prev => (prev && keys.includes(prev)) ? prev : (keys.includes(todayKey) ? todayKey : keys[0]));
  }, [vehicles, weekStartKey, weekEndKey]);

  const q = search.trim().toLowerCase();
  const matchSearch = (v) => !q || String(v.stock_number || '').toLowerCase().includes(q) || String(v.model || '').toLowerCase().includes(q);
  const dayVehicles = (byDay[selectedDay] || []).filter(matchSearch).sort((a, b) => {
    const d = (DEAL_ORDER[a.deal_status] ?? 9) - (DEAL_ORDER[b.deal_status] ?? 9);
    return d || String(a.delivery_time || '99').localeCompare(String(b.delivery_time || '99'));
  });

  async function startCar(v) {
    setBusyId(v.id);
    try { await api.melvilleMarkInProgress({ vehicle_id: v.id, stock_number: v.stock_number }); await load(); }
    catch (e) { alert(e.message || 'Could not start'); }
    finally { setBusyId(null); }
  }
  async function submitCar(v) {
    try { await api.melvilleStopTiming({ vehicle_id: v.id, stock_number: v.stock_number }); } catch { /* best-effort */ }
    onPickCar && onPickCar({ stock_number: v.stock_number, dept: null, model: v.model, colour: v.colour, melville_vehicle_id: v.id, brand: v.brand });
  }
  async function togglePause(v) {
    setBusyId(v.id);
    const body = { vehicle_id: v.id, stock_number: v.stock_number };
    try { v.paused_at ? await api.melvilleResume(body) : await api.melvillePause(body); await load(); }
    catch (e) { alert(e.message || 'Could not pause'); }
    finally { setBusyId(null); }
  }
  async function unpickCar(v) {
    if (!window.confirm(`Un-pick ${v.model || v.stock_number}? It goes back to the queue.`)) return;
    setBusyId(v.id);
    try { await api.melvilleUnpick({ vehicle_id: v.id, stock_number: v.stock_number }); await load(); }
    catch (e) { alert(e.message || 'Could not un-pick'); }
    finally { setBusyId(null); }
  }

  const now = new Date();
  const kickerKey = selectedDay && selectedDay !== 'none' ? selectedDay
    : `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;


  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      {/* ── Intro block ── */}
      <div style={{ background: '#dfe9ee', padding: '16px 20px 18px' }}>
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 1.2, color: '#7d97a5', marginBottom: 6, textTransform: 'uppercase' }}>
          {kickerDate(kickerKey)} · {DEALERSHIP}
        </div>
        {/* Two tabs — pushed apart so they don't read as one sentence. */}
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14 }}>
          <span onClick={() => setView('active')} style={{ fontSize: 24, fontWeight: 800, lineHeight: 1.05, letterSpacing: -0.5, color: view === 'active' ? NAVY : '#b6c8d2', cursor: 'pointer' }}>Detail Queue</span>
          <span onClick={() => setView('completed')} style={{ fontSize: 15, fontWeight: 800, lineHeight: 1.05, color: view === 'completed' ? NAVY : '#8ba6b4', cursor: 'pointer', background: view === 'completed' ? '#fff' : 'transparent', border: `1.5px solid ${view === 'completed' ? '#fff' : '#c3d4dc'}`, borderRadius: 999, padding: '6px 14px' }}>Completed</span>
        </div>
        {/* Week navigator — filters both the queue and the completed list. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <button onClick={() => setWeekStart(w => addDays(w, -7))} style={weekBtn}>‹</button>
          <div style={{ flex: 1, textAlign: 'center', background: '#fff', borderRadius: 12, height: 40, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', lineHeight: 1.05 }}>
            <span style={{ fontSize: 14, fontWeight: 800, color: NAVY }}>{weekLabel(weekStart)}</span>
            {weekStartKey === ymd(mondayOf(new Date())) && <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 0.5, color: TEAL }}>THIS WEEK</span>}
          </div>
          <button onClick={() => setWeekStart(w => addDays(w, 7))} style={weekBtn}>›</button>
          {weekStartKey !== ymd(mondayOf(new Date())) && (
            <button onClick={() => setWeekStart(mondayOf(new Date()))} style={{ ...weekBtn, width: 'auto', padding: '0 12px', fontSize: 11, fontWeight: 800 }}>Today</button>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 9, background: '#fff', borderRadius: 14, padding: '0 14px', height: 44 }}>
          <MagnifyingGlass size={17} color="#9ca3af" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search stock or model"
            style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 14, color: NAVY }}
          />
        </div>
      </div>

      {/* ── List area ── */}
      <div style={{ background: '#fff', padding: '16px 0 24px' }}>
        {!configured ? (
          <div style={{ margin: '0 16px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 12, padding: 18, color: '#92400E', fontSize: 13.5, lineHeight: 1.6 }}>
            <strong>Integration not configured on this server.</strong><br />
            Set <code>MELVILLE_API_URL</code> and <code>MELVILLE_API_KEY</code> to enable the queue.
          </div>
        ) : error ? (
          <div style={{ margin: '0 16px', background: '#FEE2E2', border: '1px solid #FCA5A5', borderRadius: 12, padding: 16, color: '#991B1B', fontSize: 13 }}>
            Couldn't reach the dealership: {error}
          </div>
        ) : loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: MUTED }}>Loading…</div>

        ) : view === 'completed' ? (
          /* ── COMPLETED ── */
          completed.filter(c => !q || String(c.stock_number || '').toLowerCase().includes(q) || String(c.model || '').toLowerCase().includes(q)).length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>Nothing completed this week.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {completed
                .filter(c => !q || String(c.stock_number || '').toLowerCase().includes(q) || String(c.model || '').toLowerCase().includes(q))
                .map((c, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#f4f6f8', padding: '12px 16px' }}>
                  <BrandMark brand={c.brand} avail />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 16, fontWeight: 800, color: '#1a1a2e' }}>{c.model || '—'}{c.detailer_name && <span style={{ fontSize: 12, fontWeight: 600, color: MUTED }}> · {c.detailer_name}</span>}</div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: MUTED, marginTop: 3, fontVariantNumeric: 'tabular-nums' }}>
                      {c.stock_number}{c.submitted_at ? ` · ${kickerDate(String(c.submitted_at).slice(0, 10))} ${timeHM(c.submitted_at)}` : ''}{c.duration_seconds != null ? ` · ${fmtDur(c.duration_seconds)}` : ''}
                    </div>
                  </div>
                  <CarCheckIcon size={26} color="#15803d" />
                </div>
              ))}
            </div>
          )

        ) : vehicles.length === 0 ? (
          <div style={{ margin: '0 16px', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 12, padding: 22, textAlign: 'center', color: '#15803D', fontSize: 14, fontWeight: 600 }}>
            🎉 No cars waiting for detail right now.
          </div>
        ) : (
          /* ── ACTIVE QUEUE ── */
          <>
            {dayKeys.length >= 1 && (
              <div style={{ display: 'flex', gap: 9, marginBottom: 16, padding: '0 16px', overflowX: 'auto' }}>
                {dayKeys.map(k => {
                  const sel = k === selectedDay;
                  const n = byDay[k].length;
                  let top, mid;
                  if (k === 'none') { top = 'No'; mid = 'date'; }
                  else { const [y, mo, d] = k.split('-').map(Number); top = WD[new Date(y, mo - 1, d).getDay()]; mid = String(d); }
                  return (
                    <button key={k} onClick={() => setSelectedDay(k)} style={{
                      flex: '0 0 auto', width: 64, borderRadius: 14, padding: '8px 0', cursor: 'pointer',
                      border: sel ? 'none' : '1.5px solid #e5e7eb', background: sel ? TEAL : '#fff',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
                    }}>
                      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.8, color: sel ? '#cfe0e8' : '#9ca3af' }}>{top}</span>
                      <span style={{ fontSize: 20, fontWeight: 800, lineHeight: 1.1, color: sel ? '#fff' : '#374151', fontVariantNumeric: 'tabular-nums' }}>{mid}</span>
                      <span style={{ fontSize: 10, fontWeight: 600, color: sel ? '#F5C518' : '#9ca3af' }}>{n} car{n > 1 ? 's' : ''}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {dayVehicles.length === 0 ? (
              <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No cars match.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {dayVehicles.map(v => {
                  const avail   = isAvailable(v.location);
                  const isProg  = v.status_detail === 'prog';
                  const isBusy  = busyId === v.id;
                  const deal    = v.deal_status || null;
                  const locLabel = LOCATION_LABELS[String(v.location || '').toLowerCase()] || v.location || '—';
                  const onTap = () => { if (!avail || !onPickCar || isBusy || v.paused_at) return; isProg ? submitCar(v) : startCar(v); };
                  return (
                    <div key={v.id} onClick={onTap} role={avail && onPickCar ? 'button' : undefined}
                      style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#f4f6f8', padding: '12px 16px', opacity: avail ? (isBusy ? 0.6 : 1) : 0.85, cursor: avail && onPickCar ? 'pointer' : 'default', WebkitTapHighlightColor: 'transparent' }}>
                      <BrandMark brand={v.brand} avail={avail} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 16, fontWeight: 800, color: avail ? '#1a1a2e' : '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {v.model || '—'}{v.colour && <span style={{ color: avail ? colourTint(v.colour) : '#9ca3af' }}> {v.colour}</span>}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden' }}>
                          {deal && (
                            <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.5, color: '#fff', background: avail ? (DEAL_COLOR[deal] || '#64748b') : '#d1d5db', padding: '2px 7px', borderRadius: 6, flexShrink: 0 }}>{deal}</span>
                          )}
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', fontVariantNumeric: 'tabular-nums', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {v.stock_number}{v.delivery_time ? ` · ${String(v.delivery_time).slice(0, 5)}` : ''}
                          </span>
                        </div>
                        <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                          {avail ? (
                            <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: '#dcfce7', color: '#15803d' }}>📍 {locLabel}</span>
                          ) : (
                            <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: '#e5e7eb', color: '#6b7280' }}>📍 {locLabel} · not here yet</span>
                          )}
                          {avail && isProg && (
                            v.detailer_name ? (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5, fontWeight: 800, padding: '2px 8px 2px 3px', borderRadius: 999, background: '#fef3c7', color: '#a16207' }}>
                                <span style={{ width: 15, height: 15, borderRadius: '50%', background: '#a16207', color: '#fff', fontSize: 9, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                  {String(v.detailer_name).trim().charAt(0).toUpperCase()}
                                </span>
                                {String(v.detailer_name).trim().split(' ')[0]}
                              </span>
                            ) : (
                              <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: '#fef3c7', color: '#a16207' }}>In progress</span>
                            )
                          )}
                          {avail && isProg && elapsedLabel(v, nowMs) && (
                            <span style={{ fontSize: 10.5, fontWeight: 600, color: v.paused_at ? '#a16207' : '#9ca3af', fontVariantNumeric: 'tabular-nums' }}>
                              {v.paused_at ? `${elapsedLabel(v, nowMs)} · paused` : elapsedLabel(v, nowMs)}
                            </span>
                          )}
                          {avail && isProg && onPickCar && (
                            <span onClick={(e) => { e.stopPropagation(); if (!isBusy) togglePause(v); }}
                              style={{ fontSize: 10.5, fontWeight: 800, padding: '2px 9px', borderRadius: 999, background: '#eef2f6', color: '#5b6b7b', cursor: 'pointer' }}>
                              {v.paused_at ? '▶ Resume' : '❚❚ Pause'}
                            </span>
                          )}
                          {avail && isProg && onPickCar && (
                            <span onClick={(e) => { e.stopPropagation(); if (!isBusy) unpickCar(v); }}
                              style={{ fontSize: 10.5, fontWeight: 800, padding: '2px 9px', borderRadius: 999, background: '#fee2e2', color: '#b91c1c', cursor: 'pointer' }}>✕ Un-pick</span>
                          )}
                          {['ns', 'prog'].includes(String(v.status_paint || '').toLowerCase()) && (
                            <span style={{ fontSize: 10.5, fontWeight: 800, padding: '2px 8px', borderRadius: 999, background: '#ede9fe', color: '#6d28d9' }}>🛡️ Paint Protect</span>
                          )}
                        </div>
                      </div>
                      {avail && onPickCar ? (
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5, flex: 'none',
                          padding: '8px 13px', borderRadius: 999, fontSize: 12, fontWeight: 800,
                          background: isProg ? '#fff' : TEAL,
                          color:      isProg ? '#a16207' : '#fff',
                          border:     isProg ? '1.5px solid #F5C518' : '1.5px solid transparent',
                        }}>
                          <img src={`${PUB}/queue-icons/check.png`} alt=""
                            style={{ width: 14, height: 14, filter: isProg ? undefined : 'brightness(0) invert(1)' }} />
                          {isProg ? 'Finish' : 'Start'}
                        </span>
                      ) : (
                        <img src={`${PUB}/queue-icons/lock.png`} alt="Locked" style={{ width: 22, height: 22, flex: 'none', opacity: 0.4 }} />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Admin-only detailing times */}
        {isAdmin && view === 'active' && timings.length > 0 && (
          <div style={{ margin: '20px 16px 0', background: '#fff', border: `1px solid ${LINE}`, borderRadius: 12, padding: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: NAVY, marginBottom: 10 }}>⏱ Detailing times</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {timings.slice(0, 30).map(t => {
                const running = t.submitted_at == null;
                return (
                  <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, padding: '6px 8px', borderRadius: 8, background: running ? '#FEFCE8' : '#f8fafc' }}>
                    <span style={{ fontFamily: 'monospace', fontWeight: 700, color: NAVY, minWidth: 68 }}>{t.stock_number || '—'}</span>
                    <span style={{ color: '#334155', flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {[t.brand, t.model].filter(Boolean).join(' ') || '—'}{t.detailer_name && <span style={{ color: MUTED }}> · {t.detailer_name}</span>}
                    </span>
                    <span style={{ fontWeight: 800, padding: '2px 9px', borderRadius: 999, whiteSpace: 'nowrap', background: running ? '#fef3c7' : '#e0e7ff', color: running ? '#92400e' : '#3730a3' }}>
                      {running ? `▶ ${fmtDur(t.elapsed_seconds)}` : fmtDur(t.duration_seconds)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
